import React, { useEffect } from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { DashBoardParamList, DispatchParamList } from '../../../typings/Params';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { useState } from 'react';
import TypeDropdown from './TypeDropdown';
import PriorityDropdown from './PriorityDropdown';
import StatusDropdown from './StatusDropdown';
import TagsDropdown from './TagsDropdown';
import ActingBodiesDropdown from './ActingBodiesDropdown';
import GenericUserDropdown from '../../../components/Dropdowns/GenericUserDropdown';
import moment from 'moment';
import ActingBodiesCommandsDropdown from '../../../components/Dropdowns/ActingBodiesCommandsDropdown';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import { View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import OccurrenceTypesDropdown from '../../../components/dispatch/OccurrenceTypesDropdown';
import UnitNamesDropdown from '../../../components/dispatch/UnitNamesDropdown';
import FinishReasonDropdown from '../../../components/dispatch/FinishReasonDropdown';
import LocationSelector from '../../../components/LocationSelector';
import { centralAPI } from '../../../services/central-api';
import { Slider } from '@miblanchard/react-native-slider';
import Map from '../../../components/Map';
import { useAuth } from '../../../context/Auth';
import { OccurrencePriorities, OccurrenceSituation, TriggerType } from '../../../schemas/dispatch';
import PolygonTypeDropdown from '../../../components/PolygonTypeDropdown';
import PolygonsByTypeDropdown from '../../../components/PolygonsByTypeDropdown';
import DisplacedReasonsDropdown from '../../../components/dispatch/DisplacedReasonsDropdown';
import FinishReasonSubCategoryDropdown from '../../../components/dispatch/FinishReasonSubCategoryDropdown';
import ActingBodyUnitsDropdown from './ActingBodyUnitsDropdown';


interface Props {
    filters: DispatchParamList['Occurrences'] | DashBoardParamList['DashboardOccurrences'];
    setFilters: React.Dispatch<React.SetStateAction<DispatchParamList['Occurrences'] | DashBoardParamList['DashboardOccurrences']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

function OccurrencesFilter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const { user: authenticatedUser } = useAuth();
    const navigation = useNavigation<NavigationProp<DispatchParamList, 'Occurrences'>>();
    const { styles, theme } = useStyles(styleSheet);

    const [selectedTriggerTypeFilter, setSelectedTriggerTypeFilter] = useState<(TriggerType | 'manual')[]>(filters.triggerTypeIds ?? []);
    const [selectedPriorityFilter, setSelectedPriorityFilter] = useState<OccurrencePriorities | 'all'>(filters.priority ?? 'all');
    const [selectedSituationFilter, setSelectedSituationFilter] = useState<OccurrenceSituation | 'all'>(filters.situation ?? 'all');
    const [selectedOccurrenceTypesFilter, setSelectedOccurrenceTypesFilter] = useState<number[]>(filters.occurrenceTypeId ?? []);
    const [selectedUnitNamesIds, setSelectedUnitNamesIds] = useState<number[]>(filters.unitNamesIds ?? []);
    const [selectedTagsIds, setSelectedTagsIds] = useState<number[]>(filters.tags ?? []);
    const [selectedActingBodyId, setSelectedActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [selectedMapPolygonsIds, setSelectedMapPolygonsIds] = useState<number[]>(filters.mapPolygonIds ?? []);
    const [selectedPolygonType, setSelectedPolygonType] = useState<string>(filters.polygonType || '');
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);

    const [selectedAssumedBy, setSelectedAssumedBy] = useState<number>(filters.assumedBy ?? 0);
    const [selectedTransferredBy, setSelectedTransferredBy] = useState<number>(filters.transferredBy ?? 0);
    const [selectedReceivedBy, setSelectedReceivedBy] = useState<number>(filters.receivedBy ?? 0);
    const [selectedFinishedBy, setSelectedFinishedBy] = useState<number>(filters.finishedBy ?? 0);
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endIn ? moment(filters.endIn).format('HH:mm') : '');
    const [actingBodyCommandIds, setActingBodyCommandIds] = useState<number[]>(filters.actingBodyCommandIds ?? []);
    const [withDispatch, setWithDispatch] = useState<boolean>(filters.withDispatch ?? false);
    const [withoutDispatch, setWithoutDispatch] = useState<boolean>(filters.withoutDispatch ?? false);
    const [withOccurrenceDocument, setWithOccurrenceDocument] = useState<boolean>(filters.withOccurrenceDocument ?? false);
    const [selectedDisplaceReasonId, setSelectedDisplaceReasonId] = useState<number[]>(filters.displaceReasonIds ?? []);
    const [selectedFinishReasonCategoryIds, setSelectedFinishReasonCategoryIds] = useState<number[]>(filters.finishReasonCategoryIds ?? []);
    const [selectedRadius, setSelectedRadius] = useState<number>(filters.radius ?? 1);
    const [address, setAddress] = useState('');
    const [pin, setPin] = useState<LatLng | undefined>(filters.latitude && filters.longitude ? { lat: filters.latitude, lng: filters.longitude } : undefined);

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(18);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(17);
    const [selectedFinishReasonSubCategoryIds, setSelectedFinishReasonSubCategoryIds] = useState<number[]>(filters.finishReasonSubCategoryIds ?? []);
    const [selectedActingBodyUnitIds, setSelectedActingBodyUnitIds] = useState<number[]>(filters.actingBodyUnitIds ?? []);
    const [policeReportFilter, setPoliceReportFilter] = useState(filters.policeReport ?? '');


    const handleCleanFilter = () => {
        setTextFilter('');
        setSelectedOccurrenceTypesFilter([]);
        setSelectedActingBodyId(0);
        setSelectedMapPolygonsIds([]);
        setSelectedAssumedBy(0);
        setSelectedTransferredBy(0);
        setSelectedReceivedBy(0);
        setSelectedFinishedBy(0);
        setSelectedPriorityFilter('all');
        setSelectedSituationFilter('all');
        setSelectedTriggerTypeFilter([]);
        setSelectedUnitNamesIds([]);
        setSelectedTagsIds([]);
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setActingBodyCommandIds([]);
        setWithDispatch(false);
        setWithoutDispatch(false);
        setWithOccurrenceDocument(false);
        setSelectedDisplaceReasonId([]);
        setSelectedFinishReasonCategoryIds([]);
        setSelectedRadius(1);
        setAddress('');
        setPin(undefined);
        setSelectedPolygonType('');
        setSelectedFinishReasonSubCategoryIds([]);
        setSelectedActingBodyUnitIds([]);
        setPoliceReportFilter('');

    };

    async function fetchAddress(pin: LatLng) {
        const address = await centralAPI.getAddressByPlace({ latitude: pin.lat, longitude: pin.lng });
        if (address) {
            setAddress(address);
        }
    }

    useEffect(() => {
        if (selectedFinishReasonCategoryIds.length !== 1) {
            setSelectedFinishReasonSubCategoryIds([]);
        }
    }, [selectedFinishReasonCategoryIds]);

    useEffect(() => {
        if (pin) {
            fetchAddress(pin);
        }
    }, [pin]);

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        setFilters({
            ...filters,
            situation: selectedSituationFilter !== 'all' ? selectedSituationFilter : undefined,
            priority: selectedPriorityFilter !== 'all' ? selectedPriorityFilter : undefined,
            triggerTypeIds: selectedTriggerTypeFilter.length ? selectedTriggerTypeFilter : undefined,
            occurrenceTypeId: selectedOccurrenceTypesFilter.length ? selectedOccurrenceTypesFilter : undefined,
            unitNamesIds: selectedUnitNamesIds.length ? selectedUnitNamesIds : undefined,
            textFilter: textFilter ? textFilter : undefined,
            page: 0,
            tags: selectedTagsIds.length ? selectedTagsIds : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            mapPolygonIds: selectedMapPolygonsIds.length ? selectedMapPolygonsIds : undefined,
            polygonType: selectedPolygonType ? selectedPolygonType : undefined,
            assumedBy: selectedAssumedBy !== 0 ? selectedAssumedBy : undefined,
            transferredBy: selectedTransferredBy !== 0 ? selectedTransferredBy : undefined,
            receivedBy: selectedReceivedBy !== 0 ? selectedReceivedBy : undefined,
            finishedBy: selectedFinishedBy !== 0 ? selectedFinishedBy : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            actingBodyCommandIds: actingBodyCommandIds.length ? actingBodyCommandIds : undefined,
            withDispatch: withDispatch ? withDispatch : undefined,
            withoutDispatch: withoutDispatch ? withoutDispatch : undefined,
            withOccurrenceDocument: withOccurrenceDocument ? withOccurrenceDocument : undefined,
            displaceReasonIds: selectedDisplaceReasonId.length ? selectedDisplaceReasonId : undefined,
            finishReasonCategoryIds: selectedFinishReasonCategoryIds.length ? selectedFinishReasonCategoryIds : undefined,
            latitude: pin ? pin.lat : undefined,
            longitude: pin ? pin.lng : undefined,
            radius: pin ? selectedRadius : undefined,
            finishReasonSubCategoryIds: selectedFinishReasonSubCategoryIds.length ? selectedFinishReasonSubCategoryIds : undefined,
            actingBodyUnitIds: selectedActingBodyUnitIds.length ? selectedActingBodyUnitIds : undefined,
            policeReport: policeReportFilter ? policeReportFilter : undefined,
        });

        navigation.setParams({
            situation: selectedSituationFilter !== 'all' ? selectedSituationFilter : undefined,
            priority: selectedPriorityFilter !== 'all' ? selectedPriorityFilter : undefined,
            triggerTypeIds: selectedTriggerTypeFilter.length ? selectedTriggerTypeFilter : undefined,
            occurrenceTypeId: selectedOccurrenceTypesFilter.length ? selectedOccurrenceTypesFilter : undefined,
            unitNamesIds: selectedUnitNamesIds.length ? selectedUnitNamesIds : undefined,
            textFilter: textFilter ? textFilter : undefined,
            page: 1,
            tags: selectedTagsIds.length ? selectedTagsIds : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            mapPolygonIds: selectedMapPolygonsIds.length ? selectedMapPolygonsIds : undefined,
            polygonType: selectedPolygonType ? selectedPolygonType : undefined,
            assumedBy: selectedAssumedBy !== 0 ? selectedAssumedBy : undefined,
            transferredBy: selectedTransferredBy !== 0 ? selectedTransferredBy : undefined,
            receivedBy: selectedReceivedBy !== 0 ? selectedReceivedBy : undefined,
            finishedBy: selectedFinishedBy !== 0 ? selectedFinishedBy : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            actingBodyCommandIds: actingBodyCommandIds.length ? actingBodyCommandIds : undefined,
            withDispatch: withDispatch ? withDispatch : undefined,
            withoutDispatch: withoutDispatch ? withoutDispatch : undefined,
            withOccurrenceDocument: withOccurrenceDocument ? withOccurrenceDocument : undefined,
            displaceReasonIds: selectedDisplaceReasonId.length ? selectedDisplaceReasonId : undefined,
            finishReasonCategoryIds: selectedFinishReasonCategoryIds.length ? selectedFinishReasonCategoryIds : undefined,
            latitude: pin ? pin.lat : undefined,
            longitude: pin ? pin.lng : undefined,
            radius: pin ? selectedRadius : undefined,
            actingBodyUnitIds: selectedActingBodyUnitIds.length ? selectedActingBodyUnitIds : undefined,
            policeReport: policeReportFilter ? policeReportFilter : undefined,
        });

    };

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_occurrences) {
            return selectedActingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    return (
        <Filters
            onCleanFilters={handleCleanFilter}
            onFilters={handleFilter}
            applyIsLoading={polygonPendingLoading}
            applyDisabled={!!selectedPolygonType && !selectedMapPolygonsIds.length}
        >
            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences) &&
                <ActingBodiesDropdown value={selectedActingBodyId} setValue={setSelectedActingBodyId} zIndex={19} />
            }
            <FilterInput
                label={translate('searchOccurrence')}
                placeholder={translate('typeToSearch')}
                value={textFilter}
                onChange={text => {
                    setTextFilter(text);
                }}
            />

            <View style={{ zIndex: estimatedStartZIndex }}>
                <FilterDatePicker
                    date={beginInDateFilter}
                    onChangeDate={setBeginInDateFilter}
                    time={beginInHourFilter}
                    onChangeTime={setBeginInHourFilter}
                    label={translate('createdAt')}
                    zIndex={18}
                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                    onCalendarClose={() => setEstimatedStartZIndex(19)}
                />
            </View>

            <View style={{ zIndex: estimatedFinishZIndex }}>
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={setEndInDateFilter}
                    time={endInHourFilter}
                    onChangeTime={setEndInHourFilter}
                    label={translate('to')}
                    zIndex={17}
                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                    onCalendarClose={() => setEstimatedFinishZIndex(18)}
                />
            </View>

            <PolygonTypeDropdown
                label={translate('regions')}
                dropDownDirection='BOTTOM'
                zIndex={18}
                setPolygonType={setSelectedPolygonType}
                polygonType={selectedPolygonType}
                setMapPolygonsIds={setSelectedMapPolygonsIds}
            />

            <PolygonsByTypeDropdown
                label={translate('subRegions')}
                dropDownDirection='BOTTOM'
                values={selectedMapPolygonsIds}
                setValues={setSelectedMapPolygonsIds}
                polygonType={selectedPolygonType}
                showTooltip={!selectedPolygonType}
                tooltipText={translate('selectRegionTooltip')}
                zIndex={17}
                setPendingLoading={setPolygonPendingLoading}
                disabled={!selectedPolygonType}
                required={!!selectedPolygonType}
            />

            <DisplacedReasonsDropdown
                setValue={setSelectedDisplaceReasonId}
                value={selectedDisplaceReasonId}
                zIndex={16}
            />

            <TypeDropdown value={selectedTriggerTypeFilter} setValue={setSelectedTriggerTypeFilter} zIndex={15} />

            <OccurrenceTypesDropdown
                value={selectedOccurrenceTypesFilter}
                setValue={setSelectedOccurrenceTypesFilter}
                zIndex={14}
                actingBodyId={getActingBodyId()}
            />

            <PriorityDropdown value={selectedPriorityFilter} setValue={setSelectedPriorityFilter} zIndex={13} />

            <StatusDropdown value={selectedSituationFilter} setValue={setSelectedSituationFilter} zIndex={12} />

            <UnitNamesDropdown
                value={selectedUnitNamesIds}
                setValue={setSelectedUnitNamesIds}
                actingBodyId={getActingBodyId()}
                zIndex={11} />

            <ActingBodyUnitsDropdown
                value={selectedActingBodyUnitIds}
                setValue={setSelectedActingBodyUnitIds}
                actingBodyId={getActingBodyId()}
                zIndex={10}
            />

            <TagsDropdown value={selectedTagsIds} setValue={setSelectedTagsIds} zIndex={9} />

            <FilterInput
                label={translate('policeReport')}
                placeholder={translate('typeToSearch')}
                value={policeReportFilter}
                onChange={text => {
                    setPoliceReportFilter(text);
                }}
            />

            <View style={[{ zIndex: 8, gap: 10 }]}>
                <View style={{ zIndex: 2 }}>
                    <LocationSelector
                        label={translate('findByLocation')}
                        address={address}
                        onAddressChange={setAddress}
                        onPinChange={setPin}
                    />
                </View>
                {pin ?
                    <View>
                        <View style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                            <MyAppText style={{ fontWeight: 'bold' }}>{translate('radius')}</MyAppText><MyAppText>{`: ${selectedRadius.toFixed(2)}km`}</MyAppText>
                        </View>
                        <Slider
                            animateTransitions
                            minimumValue={0.1}
                            maximumValue={3}
                            minimumTrackTintColor={theme.colors.neutral['400']}
                            thumbTintColor={theme.colors.neutral['400']}
                            maximumTrackTintColor={theme.colors.neutral['300']}
                            value={selectedRadius}
                            step={0.1}
                            onValueChange={(value) => setSelectedRadius(value[0])}
                        />
                        <View style={{ width: '100%', height: 450 }}>
                            <Map
                                pinnedMarker={pin}
                                draggable={true}
                                mapControls={false}
                                radius={selectedRadius}
                            />
                        </View>
                    </View>
                    : <></>}
            </View>

            <View>
                <MyAppText style={styles.filterLabel}>{translate('withDispatch')}</MyAppText>
                <View style={{ flexDirection: 'row' }}>
                    <FilterCheckBox
                        label={translate('yes')}
                        value={withDispatch}
                        onChange={(value) => {
                            setWithDispatch(value);
                            if (value) {
                                setWithoutDispatch(!value);
                            }
                        }}
                    />
                    <FilterCheckBox
                        label={translate('no')}
                        value={withoutDispatch}
                        onChange={(value) => {
                            setWithoutDispatch(value);
                            if (value) {
                                setWithDispatch(!value);
                            }
                        }}
                    />
                </View>
            </View>
            <View>
                <MyAppText style={styles.filterLabel}>{translate('withOccurrenceDocument')}</MyAppText>
                <View style={{ flexDirection: 'row' }}>
                    <FilterCheckBox
                        label={translate('yes')}
                        value={withOccurrenceDocument}
                        onChange={setWithOccurrenceDocument}
                    />
                </View>
            </View>
            <ActingBodiesCommandsDropdown
                value={actingBodyCommandIds}
                actingBodyId={getActingBodyId()}
                setValue={setActingBodyCommandIds}
                zIndex={7}
            />
            <GenericUserDropdown
                value={selectedAssumedBy}
                setValue={setSelectedAssumedBy}
                actingBodyId={getActingBodyId()}
                zIndex={6}
                label='assumedBy' />
            <GenericUserDropdown
                value={selectedTransferredBy}
                setValue={setSelectedTransferredBy}
                actingBodyId={getActingBodyId()}
                zIndex={5}
                label='transferredBy' />
            <GenericUserDropdown
                value={selectedReceivedBy}
                setValue={setSelectedReceivedBy}
                actingBodyId={getActingBodyId()}
                zIndex={4}
                label='receivedBy' />
            <GenericUserDropdown
                value={selectedFinishedBy}
                setValue={setSelectedFinishedBy}
                actingBodyId={getActingBodyId()}
                zIndex={3}
                label='finishedBy' />
            <FinishReasonDropdown
                value={selectedFinishReasonCategoryIds}
                setValue={setSelectedFinishReasonCategoryIds}
                actingBodyId={getActingBodyId()}
                zIndex={2}
            />
            <FinishReasonSubCategoryDropdown
                value={selectedFinishReasonSubCategoryIds}
                setValue={setSelectedFinishReasonSubCategoryIds}
                actingBodyId={getActingBodyId()}
                finishReasonCategoryId={selectedFinishReasonCategoryIds.length === 1 ? selectedFinishReasonCategoryIds[0] : undefined}
                zIndex={1}
                disabled={selectedFinishReasonCategoryIds.length !== 1}
                showTooltip={selectedFinishReasonCategoryIds.length !== 1}
                tooltipText={translate('selectOneFinishReasonTooltip')}
            />
        </Filters>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    filterLabel: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },
}));


export default OccurrencesFilter;
